/**
 * TURN CC · 26 (Design refresh) — Kanban board.
 *
 * Reads-only 5-column pipeline view aligned with the side panel design
 * language: semantic status colors, generous spacing, type filter chips,
 * clean per-column skeletons.
 *
 * Layout:
 *  - Desktop  : grid 5 cols equal width
 *  - Tablet   : grid 2 cols (columns wrap)
 *  - Mobile   : grid 1 col
 *  - Container: `p-6 bg-slate-50/50 rounded-2xl` (dark: `bg-white/[0.02]`)
 *  - Column min-height: 400px so empty columns stay balanced.
 */
import { useMemo, useState } from "react";
import { useKanbanPipeline } from "@/hooks/useKanbanPipeline";
import KanbanCard from "./KanbanCard";
import {
  AlertTriangle, CheckCircle2, Clock, Play, Send,
} from "lucide-react";
import { TYPE_META } from "./calendarMeta";
import { CALENDAR_ALL_TYPES } from "@/hooks/useCalendarEvents";

// Column meta with semantic colors (aligned on EventDetailsPanel).
const COLUMN_ORDER = ["scheduled", "running", "ready_for_review",
                      "delivered", "failed"];

const COLUMN_META = {
  scheduled: {
    label: "Scheduled",
    Icon: Clock,
    dot: "bg-slate-400 dark:bg-slate-500",
    empty: "Nothing scheduled in the next 7 days",
  },
  running: {
    label: "Running",
    Icon: Play,
    dot: "bg-blue-500 animate-pulse",
    empty: "No briefs running",
  },
  ready_for_review: {
    label: "Ready for review",
    Icon: Send,
    dot: "bg-amber-500",
    empty: "Nothing pending review",
  },
  delivered: {
    label: "Delivered",
    Icon: CheckCircle2,
    dot: "bg-emerald-500",
    empty: "No deliveries in the last 7 days",
  },
  failed: {
    label: "Failed",
    Icon: AlertTriangle,
    dot: "bg-red-500",
    empty: "No failures — nice.",
  },
};

// Kanban-only type filter (Daily/Weekly/Monthly/Quotes — no corporate).
const KANBAN_TYPES = CALENDAR_ALL_TYPES.filter((t) => t !== "corporate_event");
const KANBAN_FILTERS_KEY = "hm_kanban_filters";

function loadKanbanFilters() {
  try {
    const raw = localStorage.getItem(KANBAN_FILTERS_KEY);
    if (!raw) return KANBAN_TYPES;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return KANBAN_TYPES;
    const kept = parsed.filter((t) => KANBAN_TYPES.includes(t));
    return kept.length > 0 ? kept : KANBAN_TYPES;
  } catch {
    return KANBAN_TYPES;
  }
}

function SkeletonCard({ i }) {
  return (
    <div
      key={i}
      className="h-24 rounded-xl bg-slate-100 dark:bg-white/[0.03] animate-pulse"
    />
  );
}

function ColumnHeader({ colKey, count }) {
  const meta = COLUMN_META[colKey];
  const Icon = meta.Icon;
  return (
    <div
      data-testid={`kanban-column-header-${colKey}`}
      className="flex items-center gap-2 pb-3 mb-3 border-b border-slate-200 dark:border-white/10"
    >
      <span className={"h-1.5 w-1.5 rounded-full shrink-0 " + meta.dot} />
      <Icon className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400 shrink-0" />
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400 truncate">
        {meta.label}
      </span>
      <span
        data-testid={`kanban-column-count-${colKey}`}
        className="ml-auto text-xs font-bold text-slate-900 dark:text-slate-100 bg-slate-200 dark:bg-white/10 rounded-full px-2 py-0.5 min-w-[24px] text-center"
      >
        {count}
      </span>
    </div>
  );
}

function Column({ colKey, items, onEventClick, isPending }) {
  const meta = COLUMN_META[colKey];
  const showSkeletons = isPending && items.length === 0;
  return (
    <div
      data-testid={`kanban-column-${colKey}`}
      className="flex flex-col min-w-0 min-h-[400px]"
    >
      <ColumnHeader colKey={colKey} count={items.length} />
      <div className="flex flex-col gap-2">
        {showSkeletons ? (
          [0, 1, 2].map((i) => <SkeletonCard key={i} i={i} />)
        ) : items.length === 0 ? (
          <div className="text-xs text-slate-400 dark:text-slate-500 text-center py-8">
            {meta.empty}
          </div>
        ) : items.map((evt) => (
          <KanbanCard key={evt.id} event={evt} onClick={onEventClick} />
        ))}
      </div>
    </div>
  );
}

function TypeFilterChips({ value, onChange }) {
  const toggle = (type) => {
    if (value.includes(type)) {
      if (value.length === 1) return;
      onChange(value.filter((t) => t !== type));
    } else {
      onChange([...value, type]);
    }
  };
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="kanban-type-filters">
      {KANBAN_TYPES.map((type) => {
        const meta = TYPE_META[type];
        const Icon = meta.Icon;
        const active = value.includes(type);
        return (
          <button
            key={type}
            type="button"
            data-testid={`kanban-filter-${type}`}
            data-active={active}
            onClick={() => toggle(type)}
            className={
              "inline-flex items-center gap-1.5 rounded-full h-7 px-2.5 " +
              "text-[11px] font-medium capitalize transition-all border " +
              (active
                ? meta.chipActive + " shadow-sm"
                : "bg-transparent border-slate-200 dark:border-white/10 " +
                  "text-slate-500 dark:text-slate-400 opacity-70 " +
                  "hover:opacity-100 hover:border-slate-300 dark:hover:border-white/20 " +
                  "hover:text-slate-700 dark:hover:text-slate-200")
            }
          >
            {!active && (
              <span
                className="h-1.5 w-1.5 rounded-full shrink-0"
                style={{ background: meta.color, opacity: 0.7 }}
              />
            )}
            {Icon && <Icon className="h-3 w-3 shrink-0" />}
            {meta.label}
          </button>
        );
      })}
    </div>
  );
}

export default function KanbanBoard({ onEventClick }) {
  const { data, isPending, isFetching } = useKanbanPipeline({ days: 7 });
  const [typeFilters, setTypeFilters] = useState(loadKanbanFilters);

  // Persist filter selection.
  useMemo(() => {
    try {
      localStorage.setItem(KANBAN_FILTERS_KEY, JSON.stringify(typeFilters));
    } catch { /* ignore */ }
  }, [typeFilters]);

  const columns = useMemo(() => {
    const base = data?.columns || {};
    const out = {};
    for (const c of COLUMN_ORDER) {
      out[c] = (base[c] || []).filter((e) => typeFilters.includes(e.type));
    }
    return out;
  }, [data, typeFilters]);

  return (
    <div
      data-testid="kanban-board"
      className="rounded-2xl p-4 md:p-6 bg-slate-50/50 dark:bg-white/[0.02]"
    >
      {/* Module header */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
          Pipeline
          <span className="mx-1.5 text-slate-300 dark:text-slate-600 font-normal">·</span>
          Past 7d + Next 7d
          {isFetching && !isPending && (
            <span className="ml-2 inline-block h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse align-middle" />
          )}
        </div>
        <TypeFilterChips value={typeFilters} onChange={setTypeFilters} />
      </div>

      {/* Columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {COLUMN_ORDER.map((c) => (
          <Column
            key={c}
            colKey={c}
            items={columns[c] || []}
            onEventClick={onEventClick}
            isPending={isPending && !data}
          />
        ))}
      </div>
    </div>
  );
}
